import React, { memo } from "react";
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Tooltip,
} from "@mui/material";
import { faEllipsisV, faFileLines } from "@fortawesome/pro-solid-svg-icons";

import { FontAwesomeSvgIcon } from "../FontAwesomeSvgIcon";

// A generic selectable list item with an optional contextual menu trigger.
export type SelectableListItemProps<Item extends { id: number | string; name?: string }> = {
  /** The data object represented by this row. */
  item: Item;
  /** Currently selected item id for highlighting */
  selectedItemId?: number | string | null;
  /** When true the row is in a deleting/disabled state */
  isDeleting?: boolean;
  /** Called when the row is clicked */
  onSelect: (item: Item) => void;
  /** Called when the ellipsis icon is clicked */
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, item: Item) => void;
};

// Item button styles
const listItemButtonSx = (theme: Theme, selected: boolean, isDeleting: boolean) => ({
  height: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  borderColor: selected ? "primary.main" : "divider",
  typography: "caption",
  transition: "all 0.2s",
  borderWidth: 2,
  borderStyle: "solid",
  backgroundColor: selected ? "primary.light" : "background.paper",
  "&:hover": {
    backgroundColor: isDeleting ? undefined : selected ? "primary.light" : "action.hover",
  },
  alignItems: "center",
  py: 1,
  pr: 1,
});

const SelectableListItem = <Item extends { id: number | string; name?: string }>({
  item,
  selectedItemId,
  isDeleting = false,
  onSelect,
  onMenuOpen,
}: SelectableListItemProps<Item>) => {
  return (
    <ListItem
      disablePadding
      data-testid={`selectable-list-item-${item.id}`}
      sx={(theme) => ({
        borderRadius: theme.shape.borderRadius,
        mb: 1,
        opacity: isDeleting ? 0.5 : 1,
        transition: "opacity 0.2s",
      })}
    >
      <ListItemButton
        selected={selectedItemId === item.id}
        onClick={() => onSelect(item)}
        disabled={isDeleting}
        sx={(theme) => listItemButtonSx(theme, selectedItemId === item.id, isDeleting)}
      >
        <ListItemIcon sx={{ color: "primary.main", m: 1 }}>
          <FontAwesomeSvgIcon icon={faFileLines} />
        </ListItemIcon>
        <ListItemText sx={{ fontWeight: selectedItemId === item.id ? "bold" : "normal" }}>
          {item.name ?? String(item.id)}
        </ListItemText>
        <Tooltip title="More options">
          <IconButton
            size="small"
            aria-label="more"
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen(e, item);
            }}
            disabled={isDeleting}
            sx={{ color: "text.secondary", ml: "auto", mr: 1 }}
          >
            <FontAwesomeSvgIcon icon={faEllipsisV} />
          </IconButton>
        </Tooltip>
      </ListItemButton>
    </ListItem>
  );
};

export default memo(SelectableListItem);
